import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const sessionId = params.get("session_id");
  const [state, setState] = useState("confirming");

  useEffect(() => {
    if (!orderId && !sessionId) {
      setState("error");
      return;
    }
    const url = orderId
      ? `${API}/payments/status/${orderId}`
      : `${API}/payments/stripe/status/${sessionId}`;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(url);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) {
        // keep polling
      }
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [orderId, sessionId]);

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="text-center max-w-lg"
      >
        {state === "confirming" && (
          <>
            <Loader2 className="h-8 w-8 text-gold animate-spin mx-auto" data-testid="payment-confirming-spinner" />
            <h1 className="mt-8 font-serif text-4xl font-light">Confirming your order…</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-gold/50">
              <Check className="h-7 w-7 text-gold" />
            </span>
            <h1 className="mt-8 font-serif text-5xl font-light" data-testid="payment-success-heading">
              Your stance is <span className="italic gold-text-gradient">on its way.</span>
            </h1>
            <p className="mt-6 text-ash leading-relaxed">
              Payment confirmed. Your RayStance frames are being prepared with the care they deserve.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <h1 className="font-serif text-4xl font-light" data-testid="payment-pending-heading">Almost there.</h1>
            <p className="mt-6 text-ash leading-relaxed">
              Your payment is still being confirmed by PayPal. It will finalize shortly.
            </p>
          </>
        )}
        {state === "error" && (
          <h1 className="font-serif text-4xl font-light" data-testid="payment-error-heading">
            We couldn't find that order.
          </h1>
        )}
        <Link
          to="/"
          className="mt-12 inline-block rounded-full border border-gold/60 px-10 py-4 text-xs uppercase tracking-[0.25em] text-gold hover:bg-gold hover:text-ink transition-colors duration-500"
          data-testid="payment-success-home-link"
        >
          Return to RayStance
        </Link>
      </motion.div>
    </div>
  );
}
